<template>
  <b-card no-body class="processing-card">
    <b-card-header class="processing-header">
      <div class="header-title">
        <i class="fas fa-cogs mr-2"></i>
        <strong>Processamento - Informações Internas</strong>
      </div>
      <div class="header-actions">
        <b-button
          v-if="canEdit"
          :disabled="loadingEvents"
          variant="primary"
          size="sm"
          @click="$emit('add-event')"
        >
          <i class="fas fa-plus mr-1"></i>
          Adicionar Movimentação
        </b-button>
      </div>
    </b-card-header>
    <b-card-body class="processing-body">
      <div v-if="loadingEvents" class="loading-container">
        <b-spinner></b-spinner>
        <span class="ml-2">Carregando...</span>
      </div>
      <div v-else-if="events.length === 0" class="empty-container">
        <i class="fas fa-inbox fa-2x text-muted"></i>
        <p class="mt-2 text-muted">Nenhuma movimentação encontrada</p>
      </div>
      <div v-else class="events-container">
        <b-card
          v-for="event in events"
          :id="`event-${event._id}`"
          :key="event._id"
          no-body
          class="event-card"
        >
          <b-card-body class="event-body">
            <div class="event-header">
              <span class="event-number">#{{ event.number }}</span>
              <span class="event-type">{{
                labelOmbudsmanEventType(event.type)
              }}</span>
            </div>

            <div
              v-if="event.type === OmbudsmanEventType.STATUS_CHANGE"
              class="status-change"
            >
              <label class="status-label">
                <strong>Novo Status: </strong>
                <b-badge :variant="getBadgeColor(event.status)">
                  {{ getStatusText(event.status) }}
                </b-badge>
              </label>
            </div>

            <template
              v-if="
                event.type === OmbudsmanEventType.STATUS_CHANGE &&
                event.status === OmbudsmanStatus.CONCLUDED
              "
            >
              <div class="closure-classification">
                <label class="classification-label">
                  <strong>Classificação: </strong>
                  {{
                    classificationMaps.ombudsmanClosure[event.ombudsmanClosure]
                  }}
                </label>
              </div>
            </template>

            <div
              v-if="
                event.type !== OmbudsmanEventType.MONITORING_RESPONSE_ENABLED &&
                event.type !== OmbudsmanEventType.MONITORING_RESPONSE_DISABLED
              "
              class="event-description"
            >
              <label><strong>Descrição:</strong></label>
              <p>{{ event.description }}</p>
            </div>

            <template v-if="event.type === OmbudsmanEventType.WITH_ATTACHMENT">
              <div
                v-if="event.files && event.files.length > 0"
                class="event-files"
              >
                <b-list-group>
                  <b-list-group-item
                    v-for="(file, idx) in event.files"
                    :key="idx"
                    class="d-flex justify-content-between align-items-center file-item"
                  >
                    <span class="file-name">{{ file.name }}</span>
                    <b-button
                      variant="link"
                      :href="`/api/ombudsman/${event.ombudsman}/events/${event._id}/file/${file._id}`"
                      target="_blank"
                      class="download-btn"
                    >
                      <i class="fa fa-download" aria-hidden="true"></i>
                      Download
                    </b-button>
                  </b-list-group-item>
                </b-list-group>
              </div>
              <b-alert v-else show variant="warning" class="file-warning">
                Arquivo não recebido!
              </b-alert>
            </template>

            <div
              v-if="event.pendency && event.pendency.user"
              :class="{ 'text-danger': !event.pendency.resolvedBy }"
              class="pendency-info"
            >
              <label class="pendency-label">
                <strong>Pendência para: </strong>
                <template v-if="companyUsers[event.pendency.user]">
                  <i class="fa fa-user" aria-hidden="true"></i>
                  {{ companyUsers[event.pendency.user].name }}
                  {{ companyUsers[event.pendency.user].surname }}
                </template>
                <template v-else>
                  <i class="fa fa-user-tie" aria-hidden="true"></i>
                  {{ managedByCompanyUsers[event.pendency.user].name }}
                  {{ managedByCompanyUsers[event.pendency.user].surname }}
                </template>
              </label>
              <div v-if="event.pendency.dueDate" class="pendency-due">
                <label
                  ><strong>Prazo: </strong
                  >{{ event.pendency.dueDate | date }}</label
                >
              </div>
              <div class="pendency-actions">
                <b-button
                  v-if="!event.pendency.resolvedBy && canEdit"
                  variant="outline-danger"
                  size="sm"
                  @click="$emit('resolve-pendency', event)"
                >
                  Resolver
                </b-button>
                <label v-else-if="canEdit" class="resolved-label">
                  Resolvida pela movimentação
                  <b-button
                    variant="link"
                    class="p-0 pb-1"
                    @click="$emit('show-event', event.pendency.resolvedBy)"
                  >
                    #{{ getEventNumber(event.pendency.resolvedBy) }}
                  </b-button>
                </label>
              </div>
            </div>

            <div
              v-if="event.pendency && event.pendency.resolves"
              class="resolves-pendency"
            >
              <label class="resolves-label">
                Resolve a pendência da movimentação
                <b-button
                  variant="link"
                  class="p-0 pb-1 font-italic"
                  @click="$emit('show-event', event.pendency.resolves)"
                >
                  #{{ getEventNumber(event.pendency.resolves) }}
                </b-button>
              </label>
            </div>

            <div
              v-if="event.type === OmbudsmanEventType.TASK"
              :class="{ 'text-danger': !event.task.resolvedBy }"
              class="pendency-info"
            >
              <div v-if="event.task.dueDate" class="pendency-due">
                <label>
                  <strong>Prazo: </strong>{{ event.task.dueDate | date }}
                </label>
              </div>
              <div class="pendency-actions">
                <b-button
                  v-if="!event.task.resolvedBy && canEdit"
                  variant="outline-danger"
                  size="sm"
                  @click="$emit('resolve-task', event)"
                >
                  Resolver
                </b-button>
                <b-button
                  v-if="!event.task.resolvedBy && canEdit"
                  size="sm"
                  variant="outline-warning"
                  class="ml-2"
                  @click="$emit('resend-task', event)"
                >
                  Reenviar e-mail
                </b-button>
                <label v-if="event.task.resolvedBy" class="resolved-label">
                  Resolvida pela movimentação
                  <b-button
                    variant="link"
                    class="p-0 pb-1"
                    @click="$emit('show-event', event.task.resolvedBy)"
                  >
                    #{{ getEventNumber(event.task.resolvedBy) }}
                  </b-button>
                </label>
              </div>
            </div>

            <div
              v-if="event.task && event.task.resolves"
              class="resolves-pendency"
            >
              <label class="resolves-label">
                Resolve a tarefa de terceiros da movimentação
                <b-button
                  variant="link"
                  class="p-0 pb-1 font-italic"
                  @click="$emit('show-event', event.task.resolves)"
                >
                  #{{ getEventNumber(event.task.resolves) }}
                </b-button>
              </label>
            </div>

            <div class="event-footer">
              <small class="text-muted">
                <i class="fa fa-calendar-alt" aria-hidden="true"></i>
                {{ event.createdAt | datetime }} -
                <template v-if="companyUsers[event.user]">
                  <i class="fa fa-user" aria-hidden="true"></i>
                  {{ companyUsers[event.user].name }}
                  {{ companyUsers[event.user].surname }}
                </template>
                <template v-else-if="managedByCompanyUsers[event.user]">
                  <i class="fa fa-user-tie" aria-hidden="true"></i>
                  {{ managedByCompanyUsers[event.user].name }}
                  {{ managedByCompanyUsers[event.user].surname }}
                </template>
                <template v-else-if="conformitaUsers[event.user]">
                  <i class="fa fa-user-tie text-info" aria-hidden="true"></i>
                  {{ conformitaUsers[event.user].name }}
                  {{ conformitaUsers[event.user].surname }}
                </template>
                <template
                  v-else-if="event.type === OmbudsmanEventType.SOLVE_TASK"
                >
                  <i
                    class="fa fa-user-friends text-warning"
                    aria-hidden="true"
                  ></i>
                  <em> enviado para {{ event.task.email }}</em>
                </template>
                <template v-else>
                  <em>Usuário removido</em>
                </template>
              </small>
            </div>
          </b-card-body>
        </b-card>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  getBadgeColor,
  getStatusText,
  labelEventType,
} from "../../complaints/utils";
import OmbudsmanEventType from "../../../../../backend/models/ombudsman/OmbudsmanEventType.ts";
import OmbudsmanStatus from "../../../../../backend/models/ombudsman/OmbudsmanStatus.ts";

export default {
  name: "ProcessingCard",
  props: {
    loadingEvents: {
      type: Boolean,
      default: false,
    },
    events: {
      type: Array,
      default: () => [],
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
    classificationMaps: {
      type: Object,
      required: true,
    },
    companyUsers: {
      type: Object,
      default: () => ({}),
    },
    managedByCompanyUsers: {
      type: Object,
      default: () => ({}),
    },
    conformitaUsers: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      OmbudsmanEventType,
      OmbudsmanStatus,
    };
  },
  methods: {
    getBadgeColor,
    getStatusText,
    labelOmbudsmanEventType(type) {
      switch (parseInt(type)) {
        case OmbudsmanEventType.FREE_TEXT:
          return "Texto livre";
        case OmbudsmanEventType.WITH_ATTACHMENT:
          return "Com anexo";
        case OmbudsmanEventType.STATUS_CHANGE:
          return "Mudança de Status";
        case OmbudsmanEventType.MONITORING_RESPONSE_ENABLED:
          return "Resposta habilitada";
        case OmbudsmanEventType.MONITORING_RESPONSE_DISABLED:
          return "Resposta desabilitada";
        case OmbudsmanEventType.SOLVE_PENDENCY:
          return "Resolução de pendência";
        case OmbudsmanEventType.TASK:
          return "Tarefa de terceiros";
        case OmbudsmanEventType.SOLVE_TASK:
          return "Resposta de Tarefa de terceiros";
        case OmbudsmanEventType.COPY_TO_COMPLAINTS:
          return "Cópia para canal de denúncias";
        default:
          return "Tipo desconhecido";
      }
    },
    getEventNumber(id) {
      return this.events.find((e) => e._id === id)?.number || id;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "./styles.scss";
</style>
